import { IGateway, IDevice } from '../types';

export interface AppSettings {
    endpoint: string;
    pollInterval: number;
    defaultImage: string;
    defaultZoom: number;
    minZoom: number;
    maxZoom: number;
}

export const parseMarkdownConfig = (content: string): any => {
    const lines = content.split('\n');
    const result: any = {};
    let currentSection = '';
    let currentItem: any = {};

    for (const line of lines) {
        const trimmed = line.trim();

        if (trimmed.startsWith('## ')) {
            // Save previous item if exists
            if (currentSection && Object.keys(currentItem).length > 0) {
                if (!result[currentSection]) result[currentSection] = [];
                result[currentSection].push(currentItem);
            }

            currentSection = trimmed.substring(3);
            currentItem = {};
        } else if (trimmed.startsWith('- ')) {
            const keyValue = trimmed.substring(2);
            const colonIndex = keyValue.indexOf(':');

            if (colonIndex > -1) {
                const key = keyValue.substring(0, colonIndex).trim();
                const value = keyValue.substring(colonIndex + 1).trim();

                if (key === 'position') {
                    currentItem[key] = {};
                } else {
                    currentItem[key] = isNaN(Number(value)) ? value : Number(value);
                }
            }
        } else if (trimmed.startsWith('  - ')) {
            // Handle nested items (like position coordinates)
            const keyValue = trimmed.substring(4);
            const colonIndex = keyValue.indexOf(':');

            if (colonIndex > -1) {
                const key = keyValue.substring(0, colonIndex).trim();
                const value = keyValue.substring(colonIndex + 1).trim();

                if (currentItem.position && typeof currentItem.position === 'object') {
                    currentItem.position[key] = Number(value);
                }
            }
        }
    }

    // Save last item
    if (currentSection && Object.keys(currentItem).length > 0) {
        if (!result[currentSection]) result[currentSection] = [];
        result[currentSection].push(currentItem);
    }

    return result;
};

export const loadSettings = async (): Promise<AppSettings> => {
    try {
        const response = await fetch('/config/settings.md');
        const content = await response.text();
        const parsed = parseMarkdownConfig(content);

        const apiConfig = parsed['API Configuration']?.[0] || {};
        const mapConfig = parsed['Map Settings']?.[0] || {};

        return {
            endpoint: apiConfig.endpoint || 'http://localhost:3051/api/devices',
            pollInterval: apiConfig.pollInterval || 5000,
            defaultImage: mapConfig.defaultImage || '/maps/floor1.png',
            defaultZoom: mapConfig.defaultZoom || 1,
            minZoom: mapConfig.minZoom || 0.5,
            maxZoom: mapConfig.maxZoom || 3,
        };
    } catch (error) {
        // console.error('Failed to load settings:', error);
        return {
            endpoint: 'http://localhost:3051/api/devices',
            pollInterval: 5000,
            defaultImage: '/maps/floor1.png',
            defaultZoom: 1,
            minZoom: 0.5,
            maxZoom: 3,
        };
    }
};

export const loadGateways = async (): Promise<IGateway[]> => {
    try {
        const response = await fetch('/config/gateways.md');
        const content = await response.text();
        const parsed = parseMarkdownConfig(content);

        const gateways: IGateway[] = [];
        Object.keys(parsed).forEach(key => {
            const gateway = parsed[key][0];

            // Fix position object if x and y are at root level
            if (gateway.x !== undefined && gateway.y !== undefined) {
                gateway.position = {
                    x: gateway.x,
                    y: gateway.y
                };
                // Remove x and y from root level
                delete gateway.x;
                delete gateway.y;
            }

            if (gateway.mac && gateway.name && gateway.position && gateway.radius) {
                gateways.push({
                    mac: gateway.mac,
                    name: gateway.name,
                    position: gateway.position,
                    radius: gateway.radius,
                });
            }
        });
        return gateways;
    } catch (error) {
        // console.error('Failed to load gateways:', error);
        return [];
    }
};

export const saveGateways = async (gateways: IGateway[]): Promise<boolean> => {
    try {
        // Convert gateways array to markdown format
        let markdownContent = '';

        gateways.forEach((gateway, index) => {
            markdownContent += `## Gateway ${index + 1}\n`;
            markdownContent += `- mac: ${gateway.mac}\n`;
            markdownContent += `- name: ${gateway.name}\n`;
            markdownContent += `- position:\n`;
            markdownContent += `  - x: ${gateway.position.x}\n`;
            markdownContent += `  - y: ${gateway.position.y}\n`;
            markdownContent += `- radius: ${gateway.radius}\n`;
            if (index < gateways.length - 1) {
                markdownContent += '\n';
            }
        });

        // Note: In a real application, you would need a backend endpoint to save files
        // For now, we'll just log the content and return true to simulate success
        // console.log('Gateway configuration to save:', markdownContent);

        // In a production environment, you would make a POST request to save the file:
        // const response = await fetch('/api/config/gateways', {
        //     method: 'POST',
        //     headers: { 'Content-Type': 'text/plain' },
        //     body: markdownContent
        // });
        // return response.ok;

        return true;
    } catch (error) {
        // console.error('Failed to save gateways:', error);
        return false;
    }
};

export const loadDevices = async (): Promise<IDevice[]> => {
    try {
        const response = await fetch('/config/devices.md');
        const content = await response.text();
        const parsed = parseMarkdownConfig(content);

        const devices: IDevice[] = [];
        Object.keys(parsed).forEach(key => {
            const device = parsed[key][0];
            if (device.mac) {
                devices.push({
                    mac: device.mac,
                    name: device.name || device.mac,
                });
            }
        });

        return devices;
    } catch (error) {
        // console.error('Failed to load devices:', error);
        return [];
    }
};
